import { useEffect, useState } from 'react';
import { fmtShortDay } from '../../lib/time';
import { haptic } from '../../lib/telegram';
import { useT } from '../../lib/i18n';
import { MatchReviewSheet } from '../Dashboard';
import { api as profileApi } from '../PlayerProfile';
import { Journal, type JournalData } from '../../components/Journal';
import { api } from './api';

/**
 * Every match this client has logged, reviewed or not.
 *
 * A review used to make a match disappear: the only way in was the
 * Today inbox, which lists what still needs reviewing, so writing the
 * review was also how you lost the match. The record is the point —
 * what they played, what they wrote about it, what you wrote back —
 * and it belongs on their profile, where it stays.
 *
 * Every row opens, and a reviewed one opens into the same sheet with
 * the sliders and both notes where they were left, so a review can be
 * changed later.
 */
export function MatchList({ clientId, name }: { clientId: string; name: string }) {
  const t = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  const [open, setOpen] = useState<any | null>(null);

  const load = () => api<any[]>(`/api/coach/clients/${clientId}/matches`)
    .then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [clientId]);

  return (
    <>
      <div className="pp-card__head" style={{ margin: '14px 2px 8px' }}>
        <span className="sec2">{t('co.match.title')}</span>
        {rows && rows.length > 0 && <span className="card__when">{rows.length}</span>}
      </div>
      {rows === null ? <div className="skeleton" /> : rows.length === 0 ? (
        <div className="card pp-card"><div className="card__when">{t('co.match.none')}</div></div>
      ) : (
        <div className="pp-stack">
          {rows.map((m) => (
            <button className="card pp-card pp-matchrow" key={m.id}
              onClick={() => { haptic.tap(); setOpen({ ...m, name }); }}>
              <span className={`face face--sm ${m.result === 'won' ? 'face--won' : 'face--lost'}`}>
                {m.result === 'won' ? 'W' : 'L'}
              </span>
              <span className="pp-matchrow__who">
                <span className="pp-matchrow__line">
                  {m.result === 'won' ? t('co.review.won') : t('co.review.lost')}
                  {m.score ? ` ${m.score}` : ''}
                  {m.opponent ? ` · ${t('co.review.versus', { name: m.opponent })}` : ''}
                </span>
                <span className="card__when">
                  {fmtShortDay(new Date(m.played_on ?? m.created_at))}
                  {' · '}
                  {m.reviewed_at
                    ? t('co.match.reviewed', { day: fmtShortDay(new Date(m.reviewed_at)) })
                    : m.review_requested
                      ? <span style={{ color: 'var(--held)' }}>{t('co.match.requested')}</span>
                      : t('co.match.notReviewed')}
                </span>
                {/* Their own words, kept whether or not anyone reviewed it. */}
                {m.client_feeling && (
                  <span className="pp-matchrow__said clamp2">{m.client_feeling}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <MatchReviewSheet match={open}
          onClose={() => setOpen(null)} onDone={load} />
      )}
    </>
  );
}

/** The Journal tab's timeline: every past lesson and goal, with the private notes. */
export function CoachJournal({ clientId, name, onEdit }: { clientId: string; name: string; onEdit: (bookingId: string) => void }) {
  const [data, setData] = useState<JournalData | null>(null);
  useEffect(() => {
    profileApi<JournalData>(`/api/coach/clients/${clientId}/journal`).then(setData).catch(() => setData(null));
  }, [clientId]);
  if (data === null) return <div className="skeleton" style={{ height: 120 }} />;
  return <Journal data={data} mode="coach" clientName={name} onEdit={onEdit} />;
}
